import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { UserDTO } from '@/types/user/UserDTO';
import type { PhoneBindingMode } from '@/types/user/PhoneBindingMode';

export interface AuthState {
  user?: UserDTO;
  isAuthenticated: boolean;
  /**
   * Whether the user has just signed out of their own accord.
   *
   * It is what lets a private route tell that case apart from every other way of arriving without a session,
   * and only that case: someone who chose to leave is done, so a login form reads as though something had gone wrong,
   * and they belong on the public main page.
   * A session the server ended, and a visitor who opens a cabinet address without ever having signed in,
   * both still want the sign-in form and the page it can return them to.
   *
   * It describes a moment rather than a state worth restoring,
   * so `store.ts` clears it once the persisted state has been read back.
   */
  isSignedOutByUser: boolean;
  isPhoneBindingReminderDismissed: boolean;
  isPhoneBindingEnforced: boolean;
  /**
   * The mode of the phone-binding mount that currently has a binding under way, if any.
   */
  phoneBindingFlowOwner?: Exclude<PhoneBindingMode, 'NONE'>;
}

const initialState: AuthState = {
  user: undefined,
  isAuthenticated: false,
  isSignedOutByUser: false,
  isPhoneBindingReminderDismissed: false,
  isPhoneBindingEnforced: false,
  phoneBindingFlowOwner: undefined,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    setUser: (state: AuthState, action: PayloadAction<UserDTO | undefined>): void => {
      state.user = action.payload;
    },
    signIn: (state: AuthState, action: PayloadAction<UserDTO>): void => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isSignedOutByUser = false;
      state.isPhoneBindingReminderDismissed = false;
      state.isPhoneBindingEnforced = false;
      state.phoneBindingFlowOwner = undefined;
    },
    /**
     * Ends the session because the user chose to leave.
     */
    signOut: (state: AuthState): void => {
      state.user = undefined;
      state.isAuthenticated = false;
      state.isSignedOutByUser = true;
      state.isPhoneBindingReminderDismissed = false;
      state.isPhoneBindingEnforced = false;
      state.phoneBindingFlowOwner = undefined;
    },
    /**
     * Ends the session because the server refused a request.
     * Everything it clears, `signOut` clears too; the two differ only in where the user is sent next.
     */
    sessionExpired: (state: AuthState): void => {
      state.user = undefined;
      state.isAuthenticated = false;
      state.isSignedOutByUser = false;
      state.isPhoneBindingReminderDismissed = false;
      state.isPhoneBindingEnforced = false;
      state.phoneBindingFlowOwner = undefined;
    },
    /**
     * Forgets that the user signed out, without touching the session itself.
     * Used once the persisted state has been read back, so the flag never outlives the load that follows it.
     */
    clearSignedOutByUser: (state: AuthState): void => {
      state.isSignedOutByUser = false;
    },
    dismissPhoneBindingReminder: (state: AuthState): void => {
      state.isPhoneBindingReminderDismissed = true;
    },
    setPhoneBindingEnforced: (state: AuthState, action: PayloadAction<boolean>): void => {
      state.isPhoneBindingEnforced = action.payload;
    },
    /**
     * Records which mount has a binding under way, or clears it once none has.
     */
    setPhoneBindingFlowOwner: (
      state: AuthState,
      action: PayloadAction<Exclude<PhoneBindingMode, 'NONE'> | undefined>,
    ): void => {
      state.phoneBindingFlowOwner = action.payload;
    },
  },
});

export const {
  clearSignedOutByUser,
  dismissPhoneBindingReminder,
  sessionExpired,
  setPhoneBindingEnforced,
  setPhoneBindingFlowOwner,
  setUser,
  signIn,
  signOut,
} = authSlice.actions;

export const getCurrentUser = (state: RootState): UserDTO | undefined => {
  return state?.auth?.user ?? initialState.user;
};

export const getAuthenticated = (state: RootState): boolean => {
  return state?.auth?.isAuthenticated ?? initialState.isAuthenticated;
};

export const getSignedOutByUser = (state: RootState): boolean => {
  return state?.auth?.isSignedOutByUser ?? initialState.isSignedOutByUser;
};

export const getPhoneBindingReminderDismissed = (state: RootState): boolean => {
  return state?.auth?.isPhoneBindingReminderDismissed ?? initialState.isPhoneBindingReminderDismissed;
};

export const getPhoneBindingEnforced = (state: RootState): boolean => {
  return state?.auth?.isPhoneBindingEnforced ?? initialState.isPhoneBindingEnforced;
};

export const getPhoneBindingFlowOwner = (state: RootState): Exclude<PhoneBindingMode, 'NONE'> | undefined => {
  return state?.auth?.phoneBindingFlowOwner ?? initialState.phoneBindingFlowOwner;
};

export default authSlice.reducer;
